import {
  IConnectorSupportContract,
  IServiceResolutionResult,
  UtilityResolutionStatus,
} from "../zeos-api/contracts/UtilityIntelligence";

export type LinkPartner = "uapi" | "bayou";

export interface ConnectorLinkPlan {
  /** Skip the picker and open this partner's authorization step. The person can still change it. */
  preselectPartner?: LinkPartner;
  preselectUtilityName?: string;
  /** Restrict the picker to these partners. Nothing is pre-selected. */
  partnerFilter?: LinkPartner[];
  /** Open the bill upload path. No authorization is started. */
  manualUtilityName?: string;
  note?: string;
}

export function partnerForConnector(name?: string | null): LinkPartner | undefined {
  const normalized = name?.trim().toLowerCase() ?? "";
  if (!normalized) return undefined;
  if (normalized.includes("utilityapi") || normalized === "uapi") return "uapi";
  if (normalized.includes("bayou")) return "bayou";
  return undefined;
}

/**
 * UTL P06 for the link dialog, which links one utility at a time.
 * Plans only for the service being linked (electric unless told otherwise), from its
 * connectorSelection. A service that is not RESOLVED gets an empty plan: the picker stays as is.
 * uapiOnly (add-a-meter, task-4061) never sends the person to bill upload; it explains instead.
 */
export function planConnectorLink(
  services: IServiceResolutionResult[] | null | undefined,
  uapiOnly = false,
  serviceCode = "electric"
): ConnectorLinkPlan {
  const primary = (services ?? []).find(
    (service) =>
      service.serviceCode === serviceCode &&
      service.status === UtilityResolutionStatus.Resolved &&
      service.connectorSelection?.mode
  );
  if (!primary?.connectorSelection) {
    return {};
  }

  const utilityName = primary.utilityName?.trim() || "this utility";
  const selection = primary.connectorSelection;
  const noConnector = (message: string): ConnectorLinkPlan =>
    uapiOnly
      ? { note: `${utilityName} has no UtilityAPI connector.` }
      : { manualUtilityName: utilityName, note: message };

  if (selection.mode === "manual") {
    return noConnector(
      `${utilityName} has no data connector. Upload a bill instead of starting an authorization.`
    );
  }

  if (selection.mode === "auto") {
    const partner = partnerForConnector(selection.connectorName);
    if (!partner || (uapiOnly && partner !== "uapi")) {
      return noConnector(
        `${utilityName} has no linkable connector. Upload a bill instead of starting an authorization.`
      );
    }
    const connector = selection.connectorName?.trim() || partner;
    return {
      preselectPartner: partner,
      preselectUtilityName: utilityName,
      note: `${connector} is the only connector for ${utilityName}. You can choose a different one.`,
    };
  }

  const partners = (selection.options ?? [])
    .map((option: IConnectorSupportContract) => partnerForConnector(option.connectorName))
    .filter((partner): partner is LinkPartner => !!partner && (!uapiOnly || partner === "uapi"));
  const unique = Array.from(new Set(partners));
  if (unique.length === 0) {
    return noConnector(
      `${utilityName} has no linkable connector. Upload a bill instead of starting an authorization.`
    );
  }
  return {
    partnerFilter: unique,
    note: `More than one connector covers ${utilityName}. Choose one.`,
  };
}
